import { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Typography, CircularProgress, Divider,
  useTheme,
} from "@mui/material";
import { tokens } from "../../theme";
import DoorFrontIcon from "@mui/icons-material/DoorFront";
import WifiIcon from "@mui/icons-material/Wifi";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import secureStorage from "../../utils/secureStorage";

const PUERTAS = [
  { key: "principal", label: "Puerta Principal", ip: "192.168.88.17", color: "#1565c0" },
  { key: "lateral",   label: "Puerta Lateral",   ip: "192.168.88.254", color: "#1565c0" },
  { key: "baño",   label: "Puerta Baño",   ip: "192.168.88.65", color: "#1565c0" },
];

const pingDevice = async (ip) => {
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), 3000);
  try {
    await fetch(`http://${ip}/`, { signal: controller.signal, mode: "no-cors" });
    clearTimeout(tid);
    return true;
  } catch {
    clearTimeout(tid);
    return false;
  }
};

const openDevice = async (ip) => {
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), 4000);
  try {
    await fetch(`http://${ip}/on`, { signal: controller.signal, mode: "no-cors" });
    clearTimeout(tid);
    return true;
  } catch {
    clearTimeout(tid);
    return false;
  }
};

const StatusDot = ({ estado }) => {
  if (estado === "checking") return <CircularProgress size={14} sx={{ color: "#f59e0b" }} />;
  const map = { online: "#10b981", offline: "#ef4444", idle: "#9ca3af" };
  return (
    <Box sx={{
      width: 12, height: 12, borderRadius: "50%",
      backgroundColor: map[estado] || map.idle,
      boxShadow: estado === "online" ? "0 0 6px #10b981" : "none",
      flexShrink: 0,
    }} />
  );
};

export const AccesosContent = ({ onConfirmar, autoVerificar = true }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [estado, setEstado] = useState({ principal: "idle", lateral: "idle" });
  const [abriendo, setAbriendo] = useState(null);
  const [mensaje, setMensaje] = useState("");
  const level = secureStorage.getItem("userLevel");
  const canViewIp = Number(level) === 1;
  
  const verificarTodas = async () => {
    setEstado({ principal: "checking", lateral: "checking" });
    setMensaje("");
    const [p, l] = await Promise.all([
      pingDevice(PUERTAS[0].ip),
      pingDevice(PUERTAS[1].ip),
    ]);
    setEstado({
      principal: p ? "online" : "offline",
      lateral:   l ? "online" : "offline",
    });
  };

  useEffect(() => {
    if (autoVerificar) verificarTodas();
  }, []);

  const handleAbrirPuerta = async (puerta) => {
    setMensaje("");
    setEstado((prev) => ({ ...prev, [puerta.key]: "checking" }));
    const ok = await pingDevice(puerta.ip);
    if (!ok) {
      setEstado((prev) => ({ ...prev, [puerta.key]: "offline" }));
      setMensaje(`Sin conexión con ${puerta.label}. Verificá la red.`);
      return;
    }
    setEstado((prev) => ({ ...prev, [puerta.key]: "online" }));
    setAbriendo(puerta.key);
    const abierta = await openDevice(puerta.ip);
    setAbriendo(null);
    if (abierta) {
      setMensaje(`✓ ${puerta.label} abierta.`);
      setTimeout(() => onConfirmar?.(puerta.key), 600);
    } else {
      setMensaje(`Error al activar ${puerta.label}.`);
    }
  };

  const verificando = estado.principal === "checking" || estado.lateral === "checking";

  return (
    <Box>
      <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 700, letterSpacing: 0.5 }}>
        ESTADO DE CONEXIÓN
      </Typography>
      <Box sx={{ mt: 1, mb: 1, display: "flex", flexDirection: "column", gap: 1 }}>
        {PUERTAS.map((p) => (
          <Box key={p.key} sx={{
            display: "flex", alignItems: "center", gap: 1.5,
            p: 1.2, borderRadius: "8px",
            backgroundColor: colors.primary[400],
            border: `1px solid ${colors.primary[300]}`,
          }}>
            <StatusDot estado={estado[p.key]} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: colors.grey[100] }}>
                {p.label}
              </Typography>
              {canViewIp && (
                <Typography sx={{ fontSize: "11px", color: colors.grey[400] }}>{p.ip}</Typography>
              )}
            </Box>
            {estado[p.key] === "online"
              ? <WifiIcon sx={{ fontSize: 16, color: "#10b981" }} />
              : estado[p.key] === "offline"
                ? <WifiOffIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                : null}
          </Box>
        ))}
      </Box>
      <Button
        size="small" variant="outlined" fullWidth
        onClick={verificarTodas}
        disabled={verificando}
        sx={{ mb: 2, borderColor: "#CEDC00", color: "#CEDC00", fontSize: "12px" }}
      >
        {verificando ? "Verificando..." : "Verificar conexión"}
      </Button>

      <Divider sx={{ mb: 2, borderColor: colors.primary[300] }} />

      <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 700, letterSpacing: 0.5 }}>
        SELECCIONÁ LA PUERTA
      </Typography>
      <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {PUERTAS.map((p) => {
          const isOffline = estado[p.key] === "offline";
          const isLoading = abriendo === p.key;
          return (
            <Button
              key={p.key}
              fullWidth variant="contained"
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <LockOpenIcon />}
              onClick={() => handleAbrirPuerta(p)}
              disabled={isOffline || isLoading || abriendo !== null}
              sx={{
                backgroundColor: p.color,
                "&:hover": { backgroundColor: "#002060" },
                fontWeight: 700, py: 1.4, fontSize: "14px",
                "&.Mui-disabled": {
                  backgroundColor: colors.primary[300],
                  color: colors.grey[500],
                },
              }}
            >
              {isLoading ? "Abriendo..." : p.label}
            </Button>
          );
        })}
      </Box>

      {mensaje && (
        <Typography sx={{
          mt: 2, fontSize: "13px", textAlign: "center", fontWeight: 600,
          color: mensaje.startsWith("✓") ? "#10b981" : "#ef4444",
        }}>
          {mensaje}
        </Typography>
      )}
    </Box>
  );
};

const Accesos = ({ open: visible, onClose, onConfirmar }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Dialog open={visible} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{
        sx: { backgroundColor: colors.primary[400], backgroundImage: "none" },
      }}
    >
      <DialogTitle sx={{
        display: "flex", alignItems: "center", gap: 1, pb: 1,
        color: colors.grey[100],
        borderBottom: `1px solid ${colors.primary[300]}`,
      }}>
        <DoorFrontIcon sx={{ color: colors.blueAccent[400] }} />
        Control de Accesos
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <AccesosContent onConfirmar={onConfirmar} autoVerificar={visible} />
      </DialogContent>
      <DialogActions sx={{
        px: 3, pb: 2,
        borderTop: `1px solid ${colors.primary[300]}`,
      }}>
        <Button onClick={onClose} size="small" sx={{ color: "#ef4444" }}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default Accesos;
